import type { LibraryQuery, LibraryResponse } from "@screener/protocol";
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { fetchLibrary, type LibraryResult } from "./library-api";
import {
  EMPTY_LIBRARY_DRAFT,
  EMPTY_LIBRARY_FILTERS,
  type LibraryDraft,
  type LibraryDraftErrors,
  type LibraryFilters,
  queryOfFilters,
  validateLibraryDraft,
} from "./library-filters";

/**
 * Контроллер экрана «Библиотека» (design.md stage7-library-panel, решения
 * 1–3), тем же приёмом, что `createAlertRulesController`: логика без React,
 * с внедрённым клиентом, тестируемая без рендера. Хук `useLibrary` ниже —
 * тонкая обёртка.
 */

/**
 * `idle` — запроса ещё не было (экран не открывали). `loading` — запрос идёт;
 * прежний ответ в этом состоянии не хранится, чтобы он не выдавался за
 * ответ на новые фильтры. `invalid` и `unavailable` агрегатов не несут.
 */
export type LibraryState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ok"; response: LibraryResponse }
  | { status: "invalid" }
  /** Тариф библиотеку не даёт: экран показывает замок. */
  | { status: "plan" }
  | { status: "unavailable" };

export interface LibrarySnapshot {
  /** То, что набрано в полях. */
  draft: LibraryDraft;
  /** Ошибки черновика у полей — после неудачного `submit`, до следующей правки. */
  errors: LibraryDraftErrors;
  /** Фильтры, с которыми запрошен (или получен) показанный ответ. */
  applied: LibraryFilters;
  state: LibraryState;
}

export interface LibraryControllerDeps {
  fetchLibrary: (query: LibraryQuery, signal?: AbortSignal) => Promise<LibraryResult>;
  onSessionInvalid: () => void;
}

export interface LibraryController {
  /** Первое открытие экрана шлёт запрос без фильтров; повторные вызовы — нет. */
  activate(): void;
  /** Прерывает идущий запрос и возвращает экран к «не открывали». */
  stop(): void;
  setDraft(patch: Partial<LibraryDraft>): void;
  /** Проверяет черновик и, если он годен, запрашивает выборку. */
  submit(): void;
  /** Повторяет запрос по применённым фильтрам. */
  retry(): void;
  subscribe(listener: () => void): () => void;
  getSnapshot(): LibrarySnapshot;
}

export function createLibraryController(deps: LibraryControllerDeps): LibraryController {
  let snapshot: LibrarySnapshot = {
    draft: EMPTY_LIBRARY_DRAFT,
    errors: {},
    applied: EMPTY_LIBRARY_FILTERS,
    state: { status: "idle" },
  };
  let started = false;
  // Номер запроса: ответ с чужим номером — устаревший и молча отбрасывается
  // (design.md, решение 2, «Гонка»).
  let requestId = 0;
  let inFlight: AbortController | null = null;

  const listeners = new Set<() => void>();

  function setSnapshot(next: LibrarySnapshot): void {
    snapshot = next;
    for (const listener of listeners) {
      listener();
    }
  }

  function request(filters: LibraryFilters): void {
    inFlight?.abort();
    const controller = new AbortController();
    inFlight = controller;
    requestId += 1;
    const myId = requestId;
    setSnapshot({ ...snapshot, applied: filters, state: { status: "loading" } });
    void deps.fetchLibrary(queryOfFilters(filters), controller.signal).then((result) => {
      if (myId !== requestId) {
        return;
      }
      inFlight = null;
      if (result.status === "unauthorized") {
        deps.onSessionInvalid();
        return;
      }
      setSnapshot({
        ...snapshot,
        state: result.status === "ok" ? { status: "ok", response: result.value } : result,
      });
    });
  }

  function activate(): void {
    if (started) {
      return;
    }
    started = true;
    request(EMPTY_LIBRARY_FILTERS);
  }

  function stop(): void {
    inFlight?.abort();
    inFlight = null;
    requestId += 1;
    started = false;
    if (snapshot.state.status === "loading") {
      setSnapshot({ ...snapshot, state: { status: "idle" } });
    }
  }

  function setDraft(patch: Partial<LibraryDraft>): void {
    // Ошибка снимается только у правленого поля; ошибка диапазона — при
    // правке любой из границ, она относится к паре.
    const errors = { ...snapshot.errors };
    if ("minUsd" in patch) {
      delete errors.minUsd;
      delete errors.range;
    }
    if ("maxUsd" in patch) {
      delete errors.maxUsd;
      delete errors.range;
    }
    setSnapshot({ ...snapshot, draft: { ...snapshot.draft, ...patch }, errors });
  }

  function submit(): void {
    const result = validateLibraryDraft(snapshot.draft);
    if (!result.ok) {
      setSnapshot({ ...snapshot, errors: result.errors });
      return;
    }
    // Черновик приводится к очищенному виду: поля показывают то, что ушло.
    setSnapshot({ ...snapshot, draft: result.filters, errors: {} });
    request(result.filters);
  }

  function retry(): void {
    request(snapshot.applied);
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function getSnapshot(): LibrarySnapshot {
    return snapshot;
  }

  return { activate, stop, setDraft, submit, retry, subscribe, getSnapshot };
}

interface UseLibraryOptions {
  /** Экран виден (каркас не скрыл его атрибутом `hidden`). */
  active: boolean;
  onSessionInvalid: () => void;
}

export interface UseLibraryResult {
  snapshot: LibrarySnapshot;
  setDraft: (patch: Partial<LibraryDraft>) => void;
  submit: () => void;
  retry: () => void;
}

export function useLibrary({ active, onSessionInvalid }: UseLibraryOptions): UseLibraryResult {
  const onSessionInvalidRef = useRef(onSessionInvalid);
  useEffect(() => {
    onSessionInvalidRef.current = onSessionInvalid;
  }, [onSessionInvalid]);

  const controllerRef = useRef<LibraryController | undefined>(undefined);
  if (!controllerRef.current) {
    controllerRef.current = createLibraryController({
      fetchLibrary,
      onSessionInvalid: () => onSessionInvalidRef.current(),
    });
  }
  const controller = controllerRef.current;

  // Первый запрос — при первой активации, а не при монтировании (design.md,
  // решение 1). Возврат на экран `activate()` ничего не шлёт.
  useEffect(() => {
    if (active) {
      controller.activate();
    }
  }, [active, controller]);

  // Размонтирование (в том числе пробное, StrictMode) прерывает запрос.
  useEffect(() => () => controller.stop(), [controller]);

  const snapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
    controller.getSnapshot,
  );

  const setDraft = useCallback(
    (patch: Partial<LibraryDraft>) => controller.setDraft(patch),
    [controller],
  );
  const submit = useCallback(() => controller.submit(), [controller]);
  const retry = useCallback(() => controller.retry(), [controller]);

  return { snapshot, setDraft, submit, retry };
}
